import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AvailabilityDay,
  ConciergeRequest,
  ConciergeRequestSubmitted,
  ConciergeService,
  ContentPage,
  CreateReservationRequest,
  EventListing,
  Experience,
  Faq,
  GalleryItem,
  GuestReservation,
  LaundryGarmentCategory,
  LaundryOrder,
  LaundryOrderSubmitted,
  LaundryQuote,
  LaundryService,
  MenuCategory,
  MenuItem,
  MenuItemDetail,
  ReservationCreated,
  SiteSettings,
  Testimonial,
} from '@sazerac/design-tokens';
import { api } from './client';

/** Content changes rarely; keep it cached for the session to avoid re-fetch flicker. */
const CONTENT_STALE = 5 * 60 * 1000;

export const queryKeys = {
  site: ['site'] as const,
  page: (key: string) => ['page', key] as const,
  menu: (type: string) => ['menu', type] as const,
  menuItem: (type: string, slug: string) => ['menu', type, 'item', slug] as const,
  featured: ['menu', 'drinks', 'featured'] as const,
  experiences: (homeOnly: boolean) => ['experiences', homeOnly] as const,
  events: ['events'] as const,
  gallery: (category?: string) => ['gallery', category ?? 'All'] as const,
  faqs: ['faqs'] as const,
  testimonials: ['testimonials'] as const,
  availability: (locationId: string, date: string, partySize: number, areaId?: string | null) =>
    ['availability', locationId, date, partySize, areaId ?? 'any'] as const,
  conciergeServices: ['concierge', 'services'] as const,
  laundryServices: ['laundry', 'services'] as const,
  laundryGarments: (serviceId?: string) => ['laundry', 'garments', serviceId ?? 'none'] as const,
  guestReservation: (token: string) => ['guest', 'reservation', token] as const,
  conciergeTracking: (token: string) => ['guest', 'concierge', token] as const,
  laundryTracking: (token: string) => ['guest', 'laundry', token] as const,
};

// ------------------------------------------------------------------- CONTENT

export function useSite() {
  return useQuery({
    queryKey: queryKeys.site,
    queryFn: () => api.get<SiteSettings>('/api/public/site'),
    staleTime: CONTENT_STALE,
  });
}

export function usePage(key: string) {
  return useQuery({
    queryKey: queryKeys.page(key),
    queryFn: () => api.get<ContentPage>(`/api/public/pages/${key}`),
    staleTime: CONTENT_STALE,
    retry: false,
  });
}

export function useMenu(type: 'drinks' | 'food') {
  return useQuery({
    queryKey: queryKeys.menu(type),
    queryFn: () => api.get<MenuCategory[]>(`/api/public/menus/${type}`),
    staleTime: CONTENT_STALE,
  });
}

export function useMenuItem(type: 'drinks' | 'food', slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.menuItem(type, slug ?? ''),
    queryFn: () => api.get<MenuItemDetail>(`/api/public/menus/${type}/items/${encodeURIComponent(slug!)}`),
    enabled: Boolean(slug),
    staleTime: CONTENT_STALE,
    retry: false,
  });
}

export function useFeaturedDrinks() {
  return useQuery({
    queryKey: queryKeys.featured,
    queryFn: () => api.get<MenuItem[]>('/api/public/menus/drinks/featured', { take: 6 }),
    staleTime: CONTENT_STALE,
  });
}

export function useExperiences(homeOnly = false) {
  return useQuery({
    queryKey: queryKeys.experiences(homeOnly),
    queryFn: () => api.get<Experience[]>('/api/public/experiences', { homeOnly }),
    staleTime: CONTENT_STALE,
  });
}

export function useEvents() {
  return useQuery({
    queryKey: queryKeys.events,
    queryFn: () => api.get<EventListing[]>('/api/public/events', { upcomingOnly: true }),
    staleTime: CONTENT_STALE,
  });
}

export function useGallery(category?: string) {
  return useQuery({
    queryKey: queryKeys.gallery(category),
    queryFn: () => api.get<GalleryItem[]>('/api/public/gallery', { category }),
    staleTime: CONTENT_STALE,
  });
}

export function useFaqs() {
  return useQuery({
    queryKey: queryKeys.faqs,
    queryFn: () => api.get<Faq[]>('/api/public/faqs'),
    staleTime: CONTENT_STALE,
  });
}

export function useTestimonials() {
  return useQuery({
    queryKey: queryKeys.testimonials,
    queryFn: () => api.get<Testimonial[]>('/api/public/testimonials'),
    staleTime: CONTENT_STALE,
  });
}

// -------------------------------------------------------------- RESERVATIONS

export function useAvailability(
  locationId: string | undefined,
  date: string,
  partySize: number,
  seatingAreaId?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.availability(locationId ?? '', date, partySize, seatingAreaId),
    queryFn: () =>
      api.get<AvailabilityDay>('/api/public/reservations/availability', {
        locationId: locationId!,
        date,
        partySize,
        seatingAreaId,
      }),
    // Availability goes stale quickly — someone else may be booking the same slot.
    staleTime: 30 * 1000,
    enabled: Boolean(locationId) && Boolean(date) && enabled,
  });
}

export function useCreateReservation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReservationRequest) =>
      api.post<ReservationCreated>('/api/public/reservations', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}

export function useJoinWaitlist() {
  return useMutation({
    mutationFn: (body: {
      locationId: string;
      date: string;
      time: string;
      partySize: number;
      guestName: string;
      email: string;
      phone: string;
      notes?: string;
    }) => api.post<{ id: string; detail: string }>('/api/public/reservations/waitlist', body),
  });
}

export function useRequestAccessLink() {
  return useMutation({
    mutationFn: (body: { reference: string; contact: string }) =>
      api.post<{ detail: string }>('/api/public/reservations/access-link', body),
  });
}

export function useGuestReservation(token: string | null) {
  return useQuery({
    queryKey: queryKeys.guestReservation(token ?? ''),
    queryFn: () => api.get<GuestReservation>('/api/public/reservations/me', { token: token! }),
    enabled: Boolean(token),
    retry: false,
    staleTime: 0,
  });
}

export function useModifyReservation(token: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      date: string;
      time: string;
      partySize: number;
      preferredSeatingAreaId?: string | null;
      specialRequests?: string | null;
      occasion?: string | null;
    }) => api.put<GuestReservation>('/api/public/reservations/me', body, { token }),
    onSuccess: (data) => {
      client.setQueryData(queryKeys.guestReservation(token), data);
      void client.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}

export function useCancelReservation(token: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      api.post<GuestReservation>('/api/public/reservations/me/cancel', { reason }, { token }),
    onSuccess: (data) => {
      client.setQueryData(queryKeys.guestReservation(token), data);
      void client.invalidateQueries({ queryKey: ['availability'] });
    },
  });
}

// ------------------------------------------------------------------ CONCIERGE

export function useConciergeServices() {
  return useQuery({
    queryKey: queryKeys.conciergeServices,
    queryFn: () => api.get<ConciergeService[]>('/api/public/concierge/services'),
    staleTime: CONTENT_STALE,
  });
}

export function useSubmitConciergeRequest() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<ConciergeRequestSubmitted>('/api/public/concierge/requests', body),
  });
}

export function useConciergeTracking(token: string | null) {
  return useQuery({
    queryKey: queryKeys.conciergeTracking(token ?? ''),
    queryFn: () => api.get<ConciergeRequest>('/api/public/concierge/track', { token: token! }),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useRespondToQuote(token: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { accept: boolean; note?: string }) =>
      api.post<ConciergeRequest>('/api/public/concierge/track/quote', body, { token }),
    onSuccess: (data) => client.setQueryData(queryKeys.conciergeTracking(token), data),
  });
}

// -------------------------------------------------------------------- LAUNDRY

export function useLaundryServices() {
  return useQuery({
    queryKey: queryKeys.laundryServices,
    queryFn: () => api.get<LaundryService[]>('/api/public/laundry/services'),
    staleTime: CONTENT_STALE,
  });
}

export function useLaundryGarments(serviceId?: string) {
  return useQuery({
    queryKey: queryKeys.laundryGarments(serviceId),
    queryFn: () =>
      api.get<LaundryGarmentCategory[]>('/api/public/laundry/garment-categories', { serviceId }),
    enabled: Boolean(serviceId),
  });
}

export function useLaundryQuote() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<LaundryQuote>('/api/public/laundry/quote', body),
  });
}

export function useCreateLaundryOrder() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<LaundryOrderSubmitted>('/api/public/laundry/orders', body),
  });
}

export function useLaundryTracking(token: string | null) {
  return useQuery({
    queryKey: queryKeys.laundryTracking(token ?? ''),
    queryFn: () => api.get<LaundryOrder>('/api/public/laundry/track', { token: token! }),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useRespondToLaundryPrice(token: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { accept: boolean; note?: string }) =>
      api.post<LaundryOrder>('/api/public/laundry/track/price', body, { token }),
    onSuccess: (data) => client.setQueryData(queryKeys.laundryTracking(token), data),
  });
}

// ------------------------------------------------------------------ ENQUIRIES

export function useSubmitEventEnquiry() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ reference: string; detail: string }>('/api/public/events/enquiries', body),
  });
}

export function useSubmitContact() {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ reference: string; detail: string }>('/api/public/contact', body),
  });
}

export function useSubscribeNewsletter() {
  return useMutation({
    mutationFn: (body: { email: string; name?: string; consentGiven: boolean; source?: string }) =>
      api.post<{ detail: string }>('/api/public/newsletter', body),
  });
}

export function useUnsubscribeNewsletter() {
  return useMutation({
    mutationFn: (token: string) =>
      api.post<{ unsubscribed: boolean; detail: string }>('/api/public/newsletter/unsubscribe', undefined, { token }),
  });
}
